// Phone/CallPlayer — the recording player for the Calls detail rail and the inbox
// call card: a large play/pause, a scrubber, elapsed / total, and a speed cycle.
// Native <audio> underneath. Two ways to get the audio:
//   src     — a presigned URL already in hand (the detail read presigns once).
//   getSrc  — a list row only says has_recording (lane C: a list never presigns),
//             so the URL is fetched the first time someone presses Play
//             (GET /comms/calls/<sid>/recording-url/ → ~15-minute presigned URL).
// Give the component a key per call when using getSrc, so a new call starts fresh.
import React, { useEffect, useRef, useState } from "react";
import { Pause, Play } from "lucide-react";
import "./callmedia.css";

const RATES = [1, 1.25, 1.5, 2];
const fmt = (s) => {
  const n = Math.max(0, Math.floor(Number(s) || 0));
  return `${Math.floor(n / 60)}:${String(n % 60).padStart(2, "0")}`;
};

export default function CallPlayer({ src, getSrc, label = "recording" }) {
  const ref = useRef(null);
  const [resolved, setResolved] = useState("");
  const [loading, setLoading] = useState(false);
  const [autoplay, setAutoplay] = useState(false);
  const [playing, setPlaying] = useState(false);
  const [t, setT] = useState(0);
  const [dur, setDur] = useState(0);
  const [rate, setRate] = useState(1);
  const [err, setErr] = useState("");
  const url = src || resolved;

  useEffect(() => { setPlaying(false); setT(0); setDur(0); setErr(""); }, [url]);
  useEffect(() => { if (ref.current) ref.current.playbackRate = rate; }, [rate, url]);
  // The fetched URL has just been set as the <audio> src — start it, once.
  useEffect(() => {
    if (!autoplay || !resolved || !ref.current) return;
    setAutoplay(false);
    ref.current.play().catch(() => setErr("Couldn’t play this recording in the browser."));
  }, [autoplay, resolved]);

  const toggle = async () => {
    const a = ref.current;
    if (!a) return;
    if (!url) {
      if (!getSrc || loading) return;
      setLoading(true);
      setErr("");
      try {
        const u = await getSrc();
        if (!u) { setErr("No recording for this call."); return; }
        setResolved(u);
        setAutoplay(true);
      } catch {
        setErr("Couldn’t load this recording. Try again.");
      } finally {
        setLoading(false);
      }
      return;
    }
    if (a.paused) {
      try { await a.play(); } catch { setErr("Couldn’t play this recording in the browser."); }
    } else {
      a.pause();
    }
  };
  const seek = (e) => {
    const v = Number(e.target.value);
    if (ref.current) ref.current.currentTime = v;
    setT(v);
  };

  return (
    <div className={`cplay ${playing ? "is-playing" : ""} ${loading ? "is-loading" : ""}`}>
      <button
        type="button"
        className="cplay__btn"
        onClick={toggle}
        disabled={loading}
        aria-busy={loading || undefined}
        aria-label={loading ? `Loading ${label}` : playing ? `Pause ${label}` : `Play ${label}`}
      >
        {playing ? <Pause size={22} /> : <Play size={22} />}
      </button>
      <div className="cplay__main">
        <input
          type="range"
          className="cplay__seek"
          min={0}
          max={dur || 0}
          step={0.1}
          value={Math.min(t, dur || 0)}
          onChange={seek}
          disabled={!dur}
          aria-label={`Seek ${label}`}
        />
        <div className="cplay__meta">
          <span className="cplay__time">{fmt(t)}</span>
          <button
            type="button"
            className="cplay__rate"
            onClick={() => setRate((r) => RATES[(RATES.indexOf(r) + 1) % RATES.length])}
            aria-label={`Playback speed ${rate}x, change`}
          >
            {rate}×
          </button>
          <span className="cplay__time">{dur ? fmt(dur) : "–:––"}</span>
        </div>
        {err ? <span className="cplay__err">{err}</span> : loading ? <span className="cplay__err">Loading {label}…</span> : null}
      </div>
      <audio
        ref={ref}
        src={url || undefined}
        preload="metadata"
        onPlay={() => setPlaying(true)}
        onPause={() => setPlaying(false)}
        onEnded={() => setPlaying(false)}
        onTimeUpdate={(e) => setT(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDur(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
        onDurationChange={(e) => setDur(Number.isFinite(e.currentTarget.duration) ? e.currentTarget.duration : 0)}
        onError={() => { if (url) setErr("Recording unavailable right now."); }}
      />
    </div>
  );
}
